import { Injectable, inject } from '@angular/core'
import { HttpService } from '@core/request/http.service'
import { IS_A_STRING_AND_NOT_EMPTY, IS_NOT_SET, IS_SET } from '@core/util/check.util'
import { TO_NUMBER } from '@core/util/convert.util'
import { TO_STRING } from '@core/util/string.util'
import { jwtDecode } from 'jwt-decode'
import { DateTime } from 'luxon'
import { BehaviorSubject } from 'rxjs'

@Injectable({ providedIn: 'root' })
export class AuthService {
  HttpService = inject(HttpService)

  origin = '/login'

  selfBS = new BehaviorSubject<any>(null)

  // jwt read from cache
  public getJwt (): string | null {
    return localStorage.getItem('gateway_jwt')
  }

  public learnJwt (jwt: string): void {
    localStorage.setItem('gateway_jwt', jwt)
  }

  public forgetJwt (): void {
    localStorage.removeItem('gateway_jwt')
  }

  // jwt get + learn
  async login (
    body: { account: string, password: string, b15Days?: boolean }
  ): Promise<{ exception: any, result: any, bLoggedIn: boolean }> {
    let bLoggedIn = false

    const { exception, result } = await this.HttpService.post({ path: '/public_jwt_new', body })

    const jwt = TO_STRING(IS_NOT_SET(exception) ? result?.jwt : '')

    if (IS_A_STRING_AND_NOT_EMPTY(jwt)) {
      let bJwtDecodeError = false
      try { jwtDecode(jwt) } catch { bJwtDecodeError = true }
      if (!bJwtDecodeError) {
        this.learnJwt(jwt)
        bLoggedIn = true
      }
    }

    return { exception, result, bLoggedIn }
  }

  checkJwt (): boolean {
    let bJwt = false

    const jwt = TO_STRING(this.getJwt())

    if (IS_A_STRING_AND_NOT_EMPTY(jwt)) {
      let decoded: any
      let bJwtDecodeError = false

      try { decoded = jwtDecode(jwt) } catch { bJwtDecodeError = true }

      if (!bJwtDecodeError && IS_SET(decoded)) {
        const mExp = DateTime.fromMillis(1000 * TO_NUMBER(decoded?.exp))
        bJwt = mExp.isValid && (DateTime.now() < mExp)
      }
    }

    if (!bJwt) {
      this.forgetJwt()
    }

    return bJwt
  }

  async getUserSelf (): Promise<any> {
    const { result } = await this.HttpService.get({ path: '/us_self' })
    return result
  }

  async refreshCachedSelf (): Promise<void> {
    const userSelf = await this.getUserSelf()
    this.selfBS.next(userSelf)
  }

  getCachedSelf (): any {
    return this.selfBS.value
  }

  // jwt forget
  logout (_?: { origin?: string }): { bLoggedOut: boolean } {
    const { origin } = _ ?? {}
    this.origin = origin ?? '/login'
    this.forgetJwt()
    this.selfBS.next(null)
    return { bLoggedOut: true }
  }
}
